// @flow
import BaseComponent from '../base_component/base_component'
import {snakeCase, camelCase, isEqual} from 'lodash-es'
import formatters from './formatters';
import 'datatables.net';
import FilterContext from './filter_context';

import type {FilterFunctions} from './filter_context'
import type DataTables from 'datatables.net';

export default class DataTable extends BaseComponent {
    columnIndexes: {[string]: number};
    filterContext: FilterContext;
    params
    dismounting: boolean;
    currentRequest: object;
    reloadTimeout: ?TimeoutID

    constructor(id: number, component_name: string, options: any, extra_params: {}) {
        super(id, component_name, options, extra_params);
        let callback = this._handleFiltered.bind(this);
        this.filterContext = new FilterContext(this.id, null, callback, this.options.sync_only);
        this.load(true);
    }

    onReady() {
        if (this.filterContext.table) {
            this.filterContext.table.draw();
        }
    }

    _handleFiltered() {
        if (this.options.reload) {
            this.reload();
        }
        if (window._functions.dataTableModified) {
            window._functions.dataTableModified();
        }
    }

    reload() {
        $(this.component).find('.dataTables_scrollBody .loading').remove();
        $(this.component).find('.dataTables_scrollBody')
            .append('<div class="loading">Loading more results</div>');
        if (this.reloadTimeout) clearTimeout(this.reloadTimeout);
        this.reloadTimeout = setTimeout(() => {
            this.reloadTimeout = null;
            this.load(false, true);
        }, 400);
    }

    addFilter(column: ?string): FilterFunctions {
        return this.filterContext.add(column);
    }

    setFilter(column: ?string): FilterFunctions {
        return this.filterContext.set(column);
    }

    toggleFilter(column: ?string, isSet: boolean) {
        return isSet ? this.addFilter(column) : this.removeFilter(column);
    }

    removeFilter(column: ?string): FilterFunctions {
        return this.filterContext.clear(column);
    }

    removeFilters(column: ?string): void {
        this.filterContext.clearAll(column);
    }

    clear() {
        if (!this.filterContext.table) return;
        this.filterContext.table.destroy();
        $(this.component).find('table').html('');
    }


    functions() {
        return {
            addSearch: (term: string, col: ?string) => this.addFilter(col).search(term),
            removeSearch: (term: ?string, col: ?string) => this.removeFilter(col).search(term),
            showColumn: (name: string) => this.filterContext.toggleColumn(name, true),
            hideColumn: (name: string) => this.filterContext.toggleColumn(name, false),
            addDateFilter: (startDate: any, endDate: any) => this.addFilter('Date').dateRange(startDate, endDate),
            removeDateFilter: () => this.filterContext.table ? this.removeFilter('Date').dateRange() : null,
            toggleTag: (value: string, category: string, checked: boolean) => this
                .toggleFilter('Tags', checked).tagSearch(value, category),
            reloadData: () => this.load()
        }
    }

    load(initialLoad:boolean=false, isReload:boolean=false) {
        let self = this;
        let description = self.filterContext.getDescription();
        if (this.params && isEqual(description, this.params)) return;
        this.params = Object.assign( {}, this.getParams(), description );
        var headers: any = {'Component-Initial-Table-Load': initialLoad.toString()};
        self.currentRequest = $.ajax({
            dataType: 'json',
            headers: headers,
            url: this.options.controller,
            data: this.params,
            beforeSend : function()    {
                if (self.currentRequest != null) self.currentRequest.abort();
            },
            success: (response) => {
                if (self.dismounting) return;
                self.finishedLoading();
                let options = self.getDefaultOptions(response);
                let $component = $(self.component);
                self.clear();
                response.columns.forEach(column => {
                    $component.find('.headers').append(
                        `<th class="col_${snakeCase(column[0])}">${column[0]}</th>`);
                    self.handleFormatters(column, options, $component);
                });

            if (self.dismounting) return;
            self.filterContext.table = $(self.component).find('table').DataTable(options);
            self.filterContext.columnIndexes = self.columnIndexes;
            self.filterContext.apply(isReload);
            self.addIcons();
            let trs = self.filterContext.table.$('tr');
            Object.entries(response.extraClasses || {}).forEach(([className, rows: any]) => {
                rows.forEach(i => $(trs[i]).addClass(className));
            });
            $component.find('.action-wrapper').show();

                if (window._functions && window._functions.dataTableModified) {
                    window._functions.dataTableModified();
                }
                // Hide the header containing the page size options
                $('.dataTables_length').hide();
        }});
    }
    
    finalize() {
        this.dismounting = true;
        this.filterContext.dismount();
    }

    handleFormatters(column: Array<Map<String, String> | String>, options: any, $component: JQuery) {
        column.slice(1).forEach((formatter) => {
            let config = null;
            if (typeof(formatter) == 'object') {
                Object.keys(formatter).forEach( (fm) => {
                    config = formatter[fm]
                    this.handleFormatter(column, config, fm, options, $component)
                });
            } else {
                this.handleFormatter(column, config, formatter, options, $component)
            }
        }, this);
    }

    handleFormatter(column: Array<Map<String, String> | String>, config: any, formatter: any, options: any, $component: JQuery) {
        let formatterFunc = formatters[camelCase(formatter)];
        if (!formatterFunc) {
            throw `Could not find formatter with name ${camelCase(formatter)}`;
        }
        formatterFunc(column[0], config, options, $component);
    }

    getDefaultOptions(response: any): DataTables$Settings {
        this.columnIndexes = response.columns.reduce((acc, column, index) => {
            acc[column[0]] = index;
            return acc;
        }, {});
        let stripes = this.options.stripe ? this.options.stripe.split(';') : ['odd', 'even']
        stripes = stripes.map(name => `stripe_${this.id} ${name}`)
        return {
            data: response.data,
            columns: response.columns.map(column =>
                ({name: column[0], title: column[0], orderable: false})),
            paging: !!this.options.paging,
            pagingType: this.options.paging,
            pageLength: this.options.page_length,
            destroy: true,
            scrollX: true,
            scrollY: this.options.height ? `${this.options.height}` : '100%',
            scrollCollapse: false,
            stripeClasses: stripes,
            order: ([]: any),
            oLanguage: {
                sEmptyTable: this.options.empty_message ? this.options.empty_message : 'No data available'
            },
            dom: 'lrtp'
        };
    }

    getParams() {
        let opts = Object.assign({}, this.options);
        opts.async = true;
        Object.keys(this.extra_params || {}).forEach((param) => {
            opts[`param_${param}`] = this.extra_params[param]
        })
        return Object.keys(opts).filter( key => key.startsWith('param_') )
                     .reduce((params, key) => {
                                params[key] = opts[key]; 
                                return params
                            }, {})
    }

    addIcons() {
        let $component = $(this.component);
        $component.find('.file_type').html('<i class="fa fa-file-o" aria-hidden="true"></i>');
        $component.find('.file_type.application_pdf').html('<i class="fa fa-file-pdf-o" aria-hidden="true"></i>');
        $component.find('.file_type.text_html').html('<i class="fa fa-file-code-o" aria-hidden="true"></i>');
        $component.find('.file_type.text_plain').html('<i class="fa fa-file-o" aria-hidden="true"></i>');
        $component.find('.file_type.video_mp4').html('<i class="fa fa-file-video-o" aria-hidden="true"></i>');
        $component.find('.file_type.image_jpeg, .file_type.image_gif, .file_type.image_png')
            .html('<i class="fa fa-file-image-o" aria-hidden="true"></i>');
        $component.find('.file_type.application_vnd_ms_excel, .file_type.application_vnd_openxmlformats_officedocument_spreadsheetml_sheet')
            .html('<i class="fa fa-file-excel-o" aria-hidden="true"></i>');
    }

    finishedLoading() {
        $(this.component).find('.loading-content').hide()
    }
}
